/**
 * GameBoard - main game interface
 */

import { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useGameStore } from '../../stores/gameStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { TilePool } from './TilePool';
import { TilePile } from './TilePile';
import { WordClaimInput } from './WordClaimInput';
import { PlayerArea } from './PlayerArea';
import { EndGameResults } from './EndGameResults';
import { EmojiReactions } from './EmojiReactions';
import { hasFinePointer } from '../../utils/device';

const toggleWordToSteal = (wordId: string) => useGameStore.getState().toggleWordToSteal(wordId);

export function GameBoard() {
  const status = useGameStore((s) => s.gameState?.status ?? null);
  const playerId = useGameStore((s) => s.playerId);
  // Player IDs only (stable string compare) - PlayerArea subscribes to its own data
  const playerIds = useGameStore(useShallow((s) => s.gameState?.players.map((p) => p.id) ?? []));
  const hasSelection = useGameStore((s) => s.selectedWordsToSteal.size > 0);

  // Current user first, others in join order
  const sortedPlayerIds = useMemo(() => {
    if (!playerId || !playerIds.includes(playerId)) return playerIds;
    return [playerId, ...playerIds.filter((id) => id !== playerId)];
  }, [playerIds, playerId]);

  // Clear steal selection when clicking outside input and word areas
  useEffect(() => {
    if (!hasSelection) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest('input') ||
        target.closest('[data-player-id]') ||  // Player areas (selecting words)
        target.closest('button') ||
        target.closest('[role="dialog"]')
      ) {
        return;
      }
      useGameStore.getState().clearWordsToSteal();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [hasSelection]);

  if (!status) return null;

  // Show end game results if game has ended
  if (status === 'ended') {
    return <EndGameResults />;
  }

  return (
    <div className="game-board flex flex-col">
      {/* Main game area - tabletop felt surface */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden game-table">
        <div className="game-board-content max-w-[1600px] mx-auto py-2 sm:py-3 relative z-10">
          {/* Tile flipping section - piles flank the middle pool */}
          {/* Phones (<640px): pool full width, piles side by side underneath */}
          <div className="flex flex-wrap sm:flex-nowrap items-stretch justify-center gap-2 sm:gap-3 md:gap-4 mb-2 sm:mb-3">
            <TilePile type="vowel" />

            {/* Middle pool - grows with available width on tablets/desktops */}
            <div className="order-first basis-full sm:order-none sm:basis-auto sm:flex-1 min-w-0 max-w-3xl xl:max-w-5xl">
              <TilePool />
            </div>

            <TilePile type="consonant" />
          </div>

          {/* Word claim input - single instance: fixed bottom bar on phones, inline on md+ */}
          <ClaimBar />

          {/* Player areas - tap words to select for stealing */}
          <div className="player-areas-grid">
            {sortedPlayerIds.map((id) => (
              <PlayerArea
                key={id}
                playerId={id}
                isCurrentUser={id === playerId}
                onWordClick={toggleWordToSteal}
              />
            ))}
          </div>

          {/* Steal selection hint with cancel button */}
          {hasSelection && <StealBanner />}
        </div>
      </div>

      {/* Keyboard shortcut hint (desktop only) */}
      <KeyboardHint />

      {/* Emoji reactions overlay */}
      <EmojiReactions />
    </div>
  );
}

/**
 * Hosts the single WordClaimInput. On phones it is a fixed bottom bar whose
 * measured height is published as --claim-bar-h so the scroll area padding
 * and floating chat button never overlap it.
 */
function ClaimBar() {
  const barRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const root = document.documentElement;

    const update = () => {
      const isFixed = getComputedStyle(el).position === 'fixed';
      const h = isFixed ? Math.ceil(el.getBoundingClientRect().height) : 0;
      root.style.setProperty('--claim-bar-h', `${h}px`);
    };

    // iOS Safari overlays the on-screen keyboard instead of resizing the
    // layout viewport; lift the fixed bar so it stays above the keyboard.
    const vv = window.visualViewport;
    const updateKeyboardOffset = () => {
      if (!vv || getComputedStyle(el).position !== 'fixed') {
        el.style.transform = '';
        return;
      }
      const offset = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      el.style.transform = offset > 0 ? `translateY(-${offset}px)` : '';
    };

    update();
    updateKeyboardOffset();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    vv?.addEventListener('resize', updateKeyboardOffset);
    vv?.addEventListener('scroll', updateKeyboardOffset);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      vv?.removeEventListener('resize', updateKeyboardOffset);
      vv?.removeEventListener('scroll', updateKeyboardOffset);
      root.style.setProperty('--claim-bar-h', '0px');
    };
  }, []);

  return (
    <div
      ref={barRef}
      className="claim-bar max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:z-30 max-md:bg-white max-md:dark:bg-slate-900 max-md:border-t max-md:border-slate-200 max-md:dark:border-slate-700 max-md:shadow-[0_-4px_12px_rgba(0,0,0,0.12)] md:max-w-xl md:mx-auto md:mb-3"
    >
      <WordClaimInput />
    </div>
  );
}

/** Banner listing the words selected for stealing */
function StealBanner() {
  const selectedWords = useGameStore(
    useShallow((s) => {
      const words: string[] = [];
      if (!s.gameState) return words;
      for (const playerWords of Object.values(s.gameState.playerWords)) {
        for (const w of playerWords) {
          if (s.selectedWordsToSteal.has(w.id)) words.push(w.word);
        }
      }
      return words;
    })
  );
  const count = useGameStore((s) => s.selectedWordsToSteal.size);

  return (
    <div className="mt-2 sm:mt-4 text-center text-xs sm:text-sm font-semibold text-white bg-amber-600 py-2 sm:py-3 px-3 sm:px-4 rounded-lg shadow-lg border border-amber-400 flex items-center justify-center gap-2 flex-wrap">
      <span className="break-words min-w-0">
        Stealing: {selectedWords.join(' + ') || `${count} word(s)`}
        <span className="hidden sm:inline">
          {count === 1
            ? ' — add a middle tile and rearrange'
            : ' — add a middle tile; don\'t just join them'}
        </span>
      </span>
      <button
        onClick={() => useGameStore.getState().clearWordsToSteal()}
        className="ml-2 px-3 py-1 bg-amber-700 hover:bg-amber-800 rounded text-xs font-medium transition-colors"
        aria-label="Cancel steal selection"
      >
        Cancel
      </button>
    </div>
  );
}

/**
 * Keyboard shortcut hint that shows briefly at game start (keyboard/mouse devices only)
 */
function KeyboardHint() {
  const [visible, setVisible] = useState(true);
  const hasSeenTutorial = useSettingsStore((s) => s.hasSeenTutorial);
  const [finePointer] = useState(hasFinePointer);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(false), 8000);
    return () => clearTimeout(timer);
  }, []);

  // Only show if user has seen tutorial (so they know the basics)
  if (!visible || !hasSeenTutorial || !finePointer) return null;

  return (
    <div className="hidden md:flex fixed bottom-4 left-1/2 -translate-x-1/2 bg-slate-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm items-center gap-3 z-40">
      <span className="text-slate-300">Tip:</span>
      <span className="flex items-center gap-1">
        <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-xs font-mono">←</kbd>
        Vowel
      </span>
      <span className="flex items-center gap-1">
        <kbd className="px-1.5 py-0.5 bg-slate-700 rounded text-xs font-mono">→</kbd>
        Consonant
      </span>
      <button
        onClick={() => setVisible(false)}
        className="ml-2 text-slate-300 hover:text-white"
        aria-label="Dismiss tip"
      >
        ×
      </button>
    </div>
  );
}
